/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BuilderUserContext } from '@gh/builder-shared';

import {
  FormStateSnapshot,
  getFormStateSnapshotValue,
  markFormStateAsTouched,
  PlainImmutablePageState,
} from '@gh/common-shared';
import { ID, WithUniqueId } from '@gh/common-shared-data';
import { FormMode } from '@gh/core-ui';
import { isNotNil } from '@gh/core-util';
import { map$ } from '@gh/rx/operators';

import {
  createTaskNote,
  createTaskTodo,
  getScheduleTemplateTaskDefaultGeneralSubTab,
  getScheduleTemplateTaskNoteId,
  getScheduleTemplateTaskTodoId,
  SCHEDULE_TEMPLATE_TASK_DETAILS_INIT,
  ScheduleTemplateTaskDetails,
  ScheduleTemplateTaskDetailsGeneralSubtabName,
  ScheduleTemplateTaskDetailsNoteEdit,
  ScheduleTemplateTaskDetailsTabName,
  ScheduleTemplateTaskInfo,
  scheduleTemplateTaskNoteArr,
  ScheduleTemplateTaskNoteListItem,
  scheduleTemplateTaskTodoArr,
  ScheduleTemplateTaskTodoListItem,
  validateScheduleTemplateTaskInfo,
} from '../../resources/scheduling-schedule-template-task.model';

@Injectable()
export class SchedulingScheduleTemplateTaskDetailsFormState extends PlainImmutablePageState<ScheduleTemplateTaskDetails> {

  info$ = this.select(({ info }: ScheduleTemplateTaskDetails) => info);
  name$ = this.info$.pipe(map$(getFormStateSnapshotValue), map$(({ name }) => name));
  notes$ = this.select(({ notes }: ScheduleTemplateTaskDetails) => notes);
  todo$ = this.select(({ todo }: ScheduleTemplateTaskDetails) => todo);
  selectedTab$ = this.select(({ selectedTab }: ScheduleTemplateTaskDetails) => selectedTab);
  selectedGeneralSubtab$ = this.select(({ selectedGeneralSubtab }: ScheduleTemplateTaskDetails) => selectedGeneralSubtab);
  selectedUserResource$ = this.select(({ userId }) => userId);
  selectedCostCenterResource$ = this.select(({ supplierCostCenterId, supplierContactId }) =>
    `${supplierContactId}${supplierCostCenterId}`);

  constructor(private route: ActivatedRoute,
              private userContext: BuilderUserContext) {
    super(SCHEDULE_TEMPLATE_TASK_DETAILS_INIT);
  }

  get info(): FormStateSnapshot<ScheduleTemplateTaskInfo> {
    return this.project(({ info }: ScheduleTemplateTaskDetails) => info);
  }

  get selectedTab(): ScheduleTemplateTaskDetailsTabName {
    return this.project(({ selectedTab }: ScheduleTemplateTaskDetails) => selectedTab);
  }

  get selectedGeneralSubtab(): Maybe<ScheduleTemplateTaskDetailsGeneralSubtabName> {
    return this.project(({ selectedGeneralSubtab }: ScheduleTemplateTaskDetails) => selectedGeneralSubtab);
  }

  get isTopFormInvalid(): boolean {
    return isNotNil(validateScheduleTemplateTaskInfo(getFormStateSnapshotValue(this.value.info)));
  }

  highlightInfoSnapshot(): void {
    this.setField('info', markFormStateAsTouched(this.value.info));
  }

  build(taskDetails: ScheduleTemplateTaskDetails, editMode: boolean): void {
    this.setMode(editMode ? FormMode.EditMode : FormMode.ViewMode);
    this.setValue({
      ...taskDetails,
      selectedTab: this.selectedTab || ScheduleTemplateTaskDetailsTabName.General,
      selectedGeneralSubtab: this.selectedGeneralSubtab
        || getScheduleTemplateTaskDefaultGeneralSubTab(taskDetails),
    });
    this.resetDirty();
  }

  selectTab(tab: ScheduleTemplateTaskDetailsTabName): void {
    this.setField('selectedTab', tab);
  }

  selectGeneralSubtab(subtab: ScheduleTemplateTaskDetailsGeneralSubtabName): void {
    this.setField('selectedGeneralSubtab', subtab);
  }

  updateTaskInfoSnapshot(infoSnapshot: FormStateSnapshot<ScheduleTemplateTaskInfo>): void {
    this.setField('info', markFormStateAsTouched(infoSnapshot));
    this.setDirty(true);
  }

  updateSupplierContactId(supplierCostCenterId: ID, supplierContactId: ID): void {
    this.startChangeDetection();
    this.setFields({
      supplierCostCenterId,
      supplierContactId,
      userId: void 0,
    });
    this.setDirtyIfChanged();
  }

  updateUserId(userId: ID): void {
    this.startChangeDetection();
    this.setFields({
      supplierContactId: void 0,
      userId,
    });
    this.setDirtyIfChanged();
  }

  removeNote(note: ScheduleTemplateTaskNoteListItem): void {
    this.startChangeDetection();
    this.setField('notes', scheduleTemplateTaskNoteArr.remove(this.value.notes, getScheduleTemplateTaskNoteId(note)));
    this.setDirtyIfChanged();
  }

  updateNote(note: ScheduleTemplateTaskNoteListItem,
             { memo }: ScheduleTemplateTaskDetailsNoteEdit): void {
    this.startChangeDetection();
    this.setField(
      'notes',
      scheduleTemplateTaskNoteArr.update(
        this.value.notes,
        getScheduleTemplateTaskNoteId(note), {
          ...note,
          memo,
        },
      ));
    this.setDirtyIfChanged();
  }

  addNote({ memo }: ScheduleTemplateTaskDetailsNoteEdit): void {
    this.startChangeDetection();
    this.setField(
      'notes',
      scheduleTemplateTaskNoteArr.append(
        this.value.notes,
        createTaskNote(memo, this.userContext.data.fullName),
      ));
    this.setDirtyIfChanged();
  }

  addTodo(todo: ScheduleTemplateTaskTodoListItem): void {
    this.startChangeDetection();
    this.setField('todo', scheduleTemplateTaskTodoArr.append(this.value.todo, createTaskTodo(todo)));
    this.setDirtyIfChanged();
  }

  updateTodo(todo: WithUniqueId<ScheduleTemplateTaskTodoListItem>,
             updatedTodo: ScheduleTemplateTaskTodoListItem): void {
    this.startChangeDetection();
    this.setField('todo', scheduleTemplateTaskTodoArr.update(
      this.value.todo,
      getScheduleTemplateTaskTodoId(todo), {
        ...todo,
        ...updatedTodo,
      }));
    this.setDirtyIfChanged();
  }

  removeTodo(todo: WithUniqueId<ScheduleTemplateTaskTodoListItem>): void {
    this.startChangeDetection();
    this.setField('todo', scheduleTemplateTaskTodoArr.remove(this.value.todo, getScheduleTemplateTaskTodoId(todo)));
    this.setDirtyIfChanged();
  }
}
